import {
  ArrowRight,
  CheckCircle2,
  Clock3,
  CreditCard,
  FileText,
  ShieldCheck
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { SiteFooter } from '../components/SiteFooter';
import { SiteHeader } from '../components/SiteHeader';
import { formatScans, formatUsd, pricePerScan, scanPacks, subscriptionPlan } from '../lib/pricing';
import { siteDetails } from '../lib/site';

const includedFeatures = [
  'Campaign, client, asset, and scan-experience management',
  'Browser-based image recognition with video fallback',
  'Hosted campaign media and generated QR codes',
  'Scan, video-play, CTA-click, and engagement analytics'
];

const billingFacts = [
  {
    title: 'Clear renewal',
    copy: 'ADarveo Pro renews monthly at $99 until cancelled. Cancel before renewal to avoid the next charge.',
    Icon: CreditCard
  },
  {
    title: 'Separate scan packs',
    copy: 'Additional scan packs are one-time purchases. They do not create another recurring subscription.',
    Icon: FileText
  },
  {
    title: '30-day refund policy',
    copy: 'Eligible subscription and scan-pack purchases can be refunded within 30 days under our policy.',
    Icon: ShieldCheck
  }
];

export const Pricing = () => (
  <main className="min-h-screen bg-background text-foreground">
    <section className="pricing-hero overflow-hidden text-white">
      <SiteHeader theme="dark" />
      <div className="mx-auto max-w-4xl px-5 pb-20 pt-12 text-center sm:pb-24 sm:pt-16">
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-blue-200">Straightforward pricing</p>
        <h1 className="mt-4 font-heading text-5xl font-semibold leading-tight sm:text-6xl">
          Pay for the platform. Scale scans when you need them.
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-xl leading-8 text-white/72">
          ADarveo is self-service software for creating and measuring browser-based AR experiences.
          It does not include media buying, advertising placement, or agency services.
        </p>
      </div>
    </section>

    <section className="relative z-10 mx-auto -mt-10 grid max-w-6xl gap-6 px-5 lg:grid-cols-[0.8fr_1.2fr]">
      <article className="rounded-xl bg-[#000210] p-7 text-white shadow-2xl shadow-blue-950/20 sm:p-9">
        <p className="text-sm font-semibold uppercase tracking-[0.16em] text-blue-200">Monthly software subscription</p>
        <h2 className="mt-3 font-heading text-3xl font-semibold">{subscriptionPlan.name}</h2>
        <div className="mt-8 flex items-end gap-2">
          <span className="font-heading text-6xl font-semibold">{formatUsd(subscriptionPlan.monthlyPriceUsd)}</span>
          <span className="pb-2 text-white/58">USD / month</span>
        </div>
        <p className="mt-4 text-lg text-white/76">
          {formatScans(subscriptionPlan.includedScans)} scans included per monthly billing cycle.
        </p>
        <ul className="mt-8 space-y-4">
          {includedFeatures.map((feature) => (
            <li key={feature} className="flex gap-3 leading-6 text-white/72">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-blue-300" />
              {feature}
            </li>
          ))}
        </ul>
        <a
          className="mt-9 inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-5 py-3 font-semibold text-white transition hover:bg-blue-500"
          href="/request-access"
        >
          Start with ADarveo Pro
          <ArrowRight className="h-4 w-4" />
        </a>
        <p className="mt-4 text-center text-xs leading-5 text-white/48">
          Recurring monthly subscription. Cancel before the next billing date to stop renewal.
        </p>
      </article>

      <article className="rounded-xl border border-border bg-white p-7 shadow-xl shadow-blue-950/5 sm:p-9">
        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-primary">One-time usage</p>
            <h2 className="mt-3 font-heading text-3xl font-semibold">Additional scan packs</h2>
          </div>
          <p className="text-sm text-muted">Active subscription required</p>
        </div>

        <div className="mt-7 overflow-hidden rounded-lg border border-border">
          <div className="hidden grid-cols-[1.25fr_0.8fr_0.8fr_1fr] gap-4 bg-background px-5 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-muted sm:grid">
            <span>Pack</span>
            <span>Per scan</span>
            <span>Total</span>
            <span className="text-right">Purchase</span>
          </div>
          <div className="divide-y divide-border">
            {scanPacks.map((pack) => {
              const popular = 'popular' in pack && pack.popular;
              const invoice = 'invoice' in pack && pack.invoice;

              return (
                <div
                  key={pack.scans}
                  className={`grid items-center gap-4 px-5 py-5 sm:grid-cols-[1.25fr_0.8fr_0.8fr_1fr] ${
                    popular ? 'bg-blue-50/70' : 'bg-white'
                  }`}
                >
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-heading text-xl font-semibold">{formatScans(pack.scans)} scans</p>
                      {popular ? (
                        <span className="rounded-full bg-primary px-2.5 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-white">
                          Popular
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-1 text-sm text-muted sm:hidden">
                      ${pricePerScan(pack.scans, pack.priceUsd).toFixed(2)} per scan
                    </p>
                  </div>
                  <p className="hidden text-muted sm:block">
                    ${pricePerScan(pack.scans, pack.priceUsd).toFixed(2)}
                  </p>
                  <p className="font-heading text-xl font-semibold">{formatUsd(pack.priceUsd)}</p>
                  {invoice ? (
                    <a
                      className="inline-flex items-center justify-center rounded-md border border-primary px-4 py-2 text-sm font-semibold text-primary transition hover:bg-accent sm:justify-self-end"
                      href={`mailto:${siteDetails.supportEmail}?subject=100%2C000%20scan%20pack%20invoice`}
                    >
                      Request invoice
                    </a>
                  ) : (
                    <a
                      className="inline-flex items-center justify-center rounded-md border border-border px-4 py-2 text-sm font-semibold text-foreground transition hover:border-primary/50 hover:bg-accent sm:justify-self-end"
                      href="/request-access"
                    >
                      Request access
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <p className="mt-5 text-sm leading-6 text-muted">
          Packs are charged once and remain valid for 12 months from purchase. Monthly included scans
          reset at the end of each billing cycle and do not roll over.
        </p>
      </article>
    </section>

    <section className="mx-auto max-w-6xl px-5 py-20">
      <div className="grid gap-5 md:grid-cols-3">
        {billingFacts.map(({ title, copy, Icon }) => (
          <article key={title} className="rounded-lg border border-border bg-white p-6">
            <Icon className="h-7 w-7 text-primary" />
            <h2 className="mt-5 font-heading text-xl font-semibold">{title}</h2>
            <p className="mt-3 leading-7 text-muted">{copy}</p>
          </article>
        ))}
      </div>

      <div className="mt-12 grid gap-8 rounded-xl bg-white p-7 sm:p-10 lg:grid-cols-[0.9fr_1.1fr]">
        <div>
          <Clock3 className="h-8 w-8 text-primary" />
          <h2 className="mt-5 font-heading text-3xl font-semibold">Before you purchase</h2>
          <p className="mt-4 leading-7 text-muted">
            Prices are displayed in USD. Applicable VAT, sales tax, or other transaction taxes are
            calculated by Paddle at checkout based on the buyer’s location and tax status.
          </p>
        </div>
        <div className="space-y-4 leading-7 text-muted">
          <p>
            Paddle.com acts as Merchant of Record for ADarveo orders and handles payment processing,
            payment-related buyer assistance, and returns. Your checkout will clearly show the total
            due before you confirm an order.
          </p>
          <p>
            By completing a purchase, you agree to our <Link className="font-semibold text-primary" to="/terms">Terms</Link>{' '}
            and <Link className="font-semibold text-primary" to="/refunds">Refund Policy</Link>. For billing help,
            contact <a className="font-semibold text-primary" href={`mailto:${siteDetails.supportEmail}`}>{siteDetails.supportEmail}</a>{' '}
            or visit <a className="font-semibold text-primary" href="https://paddle.net" rel="noreferrer">Paddle buyer support</a>.
          </p>
        </div>
      </div>
    </section>

    <section className="bg-[#000210] px-5 py-20 text-white">
      <div className="mx-auto flex max-w-5xl flex-col items-start justify-between gap-7 md:flex-row md:items-center">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.16em] text-blue-200">Need help choosing?</p>
          <h2 className="mt-3 font-heading text-4xl font-semibold">Tell us how many scans you expect.</h2>
        </div>
        <Link
          className="inline-flex items-center gap-2 rounded-md bg-white px-5 py-3 font-semibold text-[#000210] transition hover:bg-blue-50"
          to="/contact"
        >
          Contact ADarveo
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </section>
    <SiteFooter />
  </main>
);

